'use client'

import { useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalculatorIcon,
  CalendarIcon,
  DownloadIcon,
  EyeIcon,
  HashIcon,
  ListIcon,
  MoreHorizontalIcon,
  PackageIcon,
  RefreshCwIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  WaypointsIcon
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { JobStatusEnum, SortEnum } from '@/enums'
import {
  useDeleteProductQrExport,
  useProductQrExports,
  useBulkDeleteProductQrExports,
  useCreateProductQrExport
} from '@/hooks'
import { useFilesStore, useProductQrExportsStore } from '@/store'
import { ProductQrExportsFilterParams } from '@/types/product-qr-export'
import {
  ConfirmationDialog,
  DeleteConfirmationDialog,
  JobStatusBadge,
  LoadingState,
  SortableHeader
} from '@/components/partials'
import { ProductQrExportsFilters } from '.'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'
import { formatDateTime, formatNumber, tOptional } from '@/lib/utils'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteProductQrExportId, setDeleteProductQrExportId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const [regenerateConfirmProductQrExport, setRegenerateConfirmProductQrExport] = useState<any | null>(null)

  const { mutate: deleteProductQrExportMutate, isPending: isDeleting } = useDeleteProductQrExport()
  const { mutate: bulkDeleteProductQrExportsMutate, isPending: isBulkDeleting } = useBulkDeleteProductQrExports()
  const { mutate: createProductQrExportMutate, isPending: isRegenerating } = useCreateProductQrExport()
  const addToDownloadQueue = useFilesStore(state => state.addToDownloadQueue)

  const selectedProductQrExportIds = useProductQrExportsStore(state => state.selectedProductQrExportIds)
  const toggleProductQrExportSelection = useProductQrExportsStore(state => state.toggleProductQrExportSelection)
  const selectAllProductQrExports = useProductQrExportsStore(state => state.selectAllProductQrExports)
  const deselectAllProductQrExports = useProductQrExportsStore(state => state.deselectAllProductQrExports)

  const filters = useMemo<ProductQrExportsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())
    const statusesParam = params.get('statuses')
    const statuses = statusesParam ? statusesParam.split(',').filter(Boolean) : undefined

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined,
      statuses
    }
  }, [searchParams])

  const { data: productQrExportsData, isLoading, error, refetch, isFetching } = useProductQrExports(filters)

  const updateUrlParams = (newFilters: Partial<ProductQrExportsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if ((key === 'createdUsers' || key === 'updatedUsers' || key === 'statuses') && Array.isArray(value)) {
          params.set(key, value.join(','))
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ProductQrExportsFilterParams>) => updateUrlParams(newFilters)
  const handleResetFilters = () => router.push('/products/export/qr')
  const handleSort = (sortBy: string, sort: SortEnum) => handleFiltersChange({ sortBy, sort, page: 1 })
  const handlePageChange = (page: number) => handleFiltersChange({ page })
  const handlePageSizeChange = (size: number) => handleFiltersChange({ size, page: 1 })
  const handleView = (id: string) => router.push(`/products/export/qr/${id}`)
  const handleDelete = (id: string) => setDeleteProductQrExportId(id)

  const handleRegenerate = (productQrExport: any) => {
    setRegenerateConfirmProductQrExport(productQrExport)
  }

  const confirmRegenerate = () => {
    const target = regenerateConfirmProductQrExport
    if (!target?.filters) {
      setRegenerateConfirmProductQrExport(null)
      return
    }
    createProductQrExportMutate(target.filters, {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['productQrExports'] })
        toast.success(t('created_successfully'))
        handleFiltersChange({ page: 1 })
        setRegenerateConfirmProductQrExport(null)
      },
      onError: (error: any) => {
        console.error('Error regenerating productQrExport:', error)
        toast.error(t('error_creating'))
        setRegenerateConfirmProductQrExport(null)
      }
    })
  }

  const renderActionItems = (productQrExport: any, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(productQrExport.id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('qr_export_details')}
        </ItemComponent>
        {productQrExport.status === JobStatusEnum.COMPLETED && productQrExport.file && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              addToDownloadQueue(productQrExport.file)
            }}>
            <DownloadIcon className="h-4 w-4" />
            {t('download')}
          </ItemComponent>
        )}
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleRegenerate(productQrExport)
          }}
          disabled={isRegenerating}>
          <RefreshCwIcon className="h-4 w-4" />
          {t('regenerate')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(productQrExport.id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteProductQrExportId) {
      deleteProductQrExportMutate(deleteProductQrExportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productQrExports'] })
          toast.success(t('deleted_successfully'))
          setDeleteProductQrExportId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting productQrExport:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteProductQrExportId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedProductQrExportIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteProductQrExports = () => {
    if (selectedProductQrExportIds.length > 0) {
      bulkDeleteProductQrExportsMutate(selectedProductQrExportIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['productQrExports'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response?.status === 422) {
            toast.error(error.response.data?.items?.ids || t('error_deleting'))
          } else {
            toast.error(t('error_deleting'))
          }
          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (productQrExportsData && productQrExportsData.items.length > 0) {
      if (selectedProductQrExportIds.length === productQrExportsData.items.length) {
        deselectAllProductQrExports()
      } else {
        selectAllProductQrExports(productQrExportsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListIcon className="h-5 w-5" />
                {t('qr_exports')}
              </CardTitle>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const currentParams = searchParams.toString()
                  router.push(`/products${currentParams ? `?${currentParams}` : ''}`)
                }}>
                <PackageIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('products')}</span>
              </Button>
              <Button onClick={() => router.push('/products/export/qr/create')}>
                <SquarePlusIcon />
                <span className="hidden items-center gap-2 lg:flex">{t('create_qr_export')}</span>
              </Button>
              {selectedProductQrExportIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedProductQrExportIds.length})</span>
                  </div>
                </Button>
              )}
              <ProductQrExportsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {productQrExportsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={
                                productQrExportsData &&
                                productQrExportsData.items.length > 0 &&
                                selectedProductQrExportIds.length === productQrExportsData.items.length
                              }
                              onCheckedChange={handleSelectAll}
                              onClick={e => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="total"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('total')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="status"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <WaypointsIcon className="h-4 w-4" />
                            {t('status')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productQrExportsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        productQrExportsData.items.map((productQrExport, index) => (
                          <ContextMenu key={productQrExport.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(productQrExport.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={selectedProductQrExportIds.includes(productQrExport.id)}
                                      onCheckedChange={() => toggleProductQrExportSelection(productQrExport.id)}
                                      onClick={e => e.stopPropagation()}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <span className="text-muted-foreground text-xs">{productQrExport.id}</span>
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productQrExport.total, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div>
                                        <JobStatusBadge status={productQrExport.status} />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {productQrExport.message ? tOptional(t, productQrExport.message) : ''}
                                    </TooltipContent>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-muted-foreground text-sm">
                                    {formatDateTime(productQrExport.createdAt, locale)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <span className="text-muted-foreground text-sm">
                                    {formatDateTime(productQrExport.updatedAt, locale)}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(productQrExport, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(productQrExport, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={productQrExportsData.page}
                  totalPages={productQrExportsData.pages}
                  totalItems={productQrExportsData.total}
                  pageSize={productQrExportsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={regenerateConfirmProductQrExport !== null}
        onCancel={() => setRegenerateConfirmProductQrExport(null)}
        onConfirm={confirmRegenerate}
        description={t('regenerate_export_confirmation')}
        confirmText={t('regenerate')}
        isLoading={isRegenerating}
      />

      <DeleteConfirmationDialog
        open={deleteProductQrExportId !== null}
        onCancel={() => setDeleteProductQrExportId(null)}
        onConfirm={confirmDeleteMessage}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteProductQrExports}
        isDeleting={isBulkDeleting}
      />
    </>
  )
}

export default List
